import React, { useState, useMemo } from 'react'
import styled, { css } from 'styled-components/macro'
import {
  Text, Button, Icon
} from 'components/atoms'
import PropTypes from 'prop-types'
import { modifiersType } from 'types'
import theme from 'services/theme.semantic';
import { UserAvatar } from 'containers'
import { applyStyleModifiers } from 'styled-components-modifiers';
import { PopoverActions } from 'components/molecules'
import { useRouterContext } from 'hooks';
import { FormattedMessage } from 'react-intl'
import { useUserContext, isUserSubwebAdmin } from 'context/UserContext';

const cardSize = '130px'

const MODIFIERS = {
  stretchHeight: () => css`
    height: 100%;
  `,
};

const SubwebCard = ({
  activeColor,
  titleShort,
  title,
  user,
  modifiers,
}) => {
  const [isHover, setHover] = useState(false)
  const [hasPopover, setHasPopover] = useState(false)
  const { history } = useRouterContext();
  const [{ user: userFromContext }] = useUserContext()

  const actions = useMemo(() => {
    const isAdmin = (userFromContext && userFromContext.admin)
      || isUserSubwebAdmin(userFromContext, titleShort)

    return [
      {
        text: <FormattedMessage id="subweb.edit" />,
        onClick: () => {
          history.push(`/${encodeURIComponent(titleShort)}`)
        },
      },
      {
        text: <FormattedMessage id="subweb.edit.menu" />,
        onClick: () => {
          history.push(`/${encodeURIComponent(titleShort)}/editor/pt/nav`)
        },
      },
      {
        text: <FormattedMessage id="resources.view" />,
        onClick: () => {
          history.push(`/${encodeURIComponent(titleShort)}/assets`)
        },
      },
      ...(isAdmin
        ? [
          {
            text: <FormattedMessage id="subweb.people" />,
            onClick: () => {
              history.push(`/${encodeURIComponent(titleShort)}/people`)
            },
          },
          {
            text: <FormattedMessage id="subweb.permissions" />,
            onClick: () => {
              history.push(`/${encodeURIComponent(titleShort)}/permissions`)
            },
          },
          {
            text: <FormattedMessage id="subweb.settings" />,
            onClick: () => {
              history.push(`/${encodeURIComponent(titleShort)}/settings`)
            },
          },
        ]
        : []
      ),
    ]
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [titleShort]
  )

  const isActive = isHover || hasPopover
  return (
    <Card
      activeColor={activeColor}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      modifiers={modifiers}
      isActive={isActive}
    >
      <Card.Top>
        <Card.Header>
          <Text modifiers={['l', 'base']}>
            {titleShort}
          </Text>
          <PopoverActions
            actions={actions}
            handler={(
              <Button modifiers={['xs4', 'invisible']} aria-label="ver mais ações">
                <Icon icon="more-horiz" height={20} color={isActive ? 'white' : 'black'} />
              </Button>
            )}
            position="bottom"
            onChangeOpen={(isOpen) => setHasPopover(isOpen)}
            align="start"
          />
        </Card.Header>
        <Card.Subtitle modifiers={['xs']}>
          {title}
        </Card.Subtitle>
      </Card.Top>
      {user && user.id && (
        <Card.Bottom>
          <Card.User>
            <UserAvatar
              id={user.id}
              name={user.name}
              modifiers={[isActive ? 'inverse' : '']}
              activeColor={activeColor}
            />
            <Info>
              <Text modifiers={['light', 'xs']}>
                {
                  user.id
                    ? 'você editou hoje'
                    : <FormattedMessage id="error.missing" />
                }
              </Text>
            </Info>
          </Card.User>
        </Card.Bottom>
      )}
    </Card>
  )
}


const Card = styled.div(props => css`
  background-color: ${props.isActive ? props.activeColor : props.theme.colors.greyLighS};
  display: flex;
  transition: all 160ms ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.xs};
  min-height: ${cardSize};
  min-width: ${cardSize};

  ${Text} {
    color: ${props.isActive ? props.theme.colors.baseInverse : props.theme.colors.base} !important;
  }
  &:hover {
      background-color: ${props.activeColor};
      ${Text} {
        color: ${props.theme.colors.baseInverse} !important;
      }
  }
  ${applyStyleModifiers(MODIFIERS)};

`)

Card.Top = styled.div`
  display: flex;
  flex-direction: column;
`
Card.Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  ${Text} {
    word-break: break-all;
    margin-top: ${props => props.theme.spacing.xs4};
  }
`

Card.Subtitle = styled(Text)`
  padding-top: ${props => props.theme.spacing.xs2};
  padding-bottom: ${props => props.theme.spacing.xs};
`

Card.Bottom = styled.div`
  display: flex;
`

Card.User = styled.div`
  width: 2rem;
  display: flex;
  width: 100%;
`

const Info = styled.div`
  padding-left: ${props => props.theme.spacing.xs2};
  display: inline-flex;
  align-items: flex-end;
`

SubwebCard.propTypes = {
  activeColor: PropTypes.string,
  title: PropTypes.string,
  titleShort: PropTypes.string,
  user: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  modifiers: modifiersType,
}

SubwebCard.defaultProps = {
  activeColor: theme.colors.primary,
  title: '',
  titleShort: '',
  user: {
    id: null,
    name: null,
  },
  modifiers: []
}

export default SubwebCard
